import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import { ExpandMore, Class } from '@mui/icons-material';


const ClassBuilder = ({
  subjectId,
  subjectName,
  targetDate,
  accumulatedEmployees = [],
  onRemoveEmployee,
  suggestedGroups,
  loading,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleCreateCourse = (group) => {
    navigate('/trainingcourses/new', {
      state: {
        prefill: {
          courseName: `${subjectName || 'Training'} - Group ${group.groupIndex}${targetDate ? ` - ${format(targetDate, 'yyyy-MM-dd')}` : ''}`,
          subjects: [subjectId],
          startDate: targetDate ? format(targetDate, 'yyyy-MM-dd') : null,
          enrollments: group.employee.map((c) => ({
            employeeCode: c.employeeCode,
            employeeName: c.employeeName,
            rank:         c.rank,
            enrolledAs:   'trainee',
          })),
        },
      },
    });
  };

  return (
    <Box>
      {accumulatedEmployees.length > 0 && (
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Manually selected employees ({accumulatedEmployees.length}):
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {accumulatedEmployees.map((c) => (
              <Chip
                key={c.employeeCode}
                label={`${c.employeeCode} (${c.rank || '-'})`}
                size="small"
                onDelete={() => onRemoveEmployee?.(c.employeeCode)}
              />
            ))}
          </Box>
          <Divider sx={{ mt: 1.5 }} />
        </Box>
      )}

      {suggestedGroups && (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            {suggestedGroups.totalEligible} employee{suggestedGroups.totalEligible !== 1 ? 's' : ''} eligible →{' '}
            {suggestedGroups.groupCount} suggested group(s)
          </Typography>

          {suggestedGroups.groups.map((group) => (
            <Accordion key={group.groupIndex} defaultExpanded={group.groupIndex === 1}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={1} width="100%">
                  <Typography variant="body2" fontWeight="bold">
                    Group {group.groupIndex}
                  </Typography>
                  <Chip
                    label={`${group.employee.length} employee${group.employee.length !== 1 ? 's' : ''}`}
                    size="small"
                  />
                  <Box flex={1} />
                  <Box display="flex" gap={0.5} mr={0.5}>
                    {isMobile ? (
                      <Tooltip title="Create Course">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleCreateCourse(group); }}
                        >
                          <Class fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Class />}
                        onClick={(e) => { e.stopPropagation(); handleCreateCourse(group); }}
                      >
                        Create Course
                      </Button>
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 480 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Rank</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Base</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Expiry</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.employee.map((c) => (
                        <TableRow key={c.employeeCode}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">{c.employeeCode}</Typography>
                          </TableCell>
                          <TableCell>{c.employeeName}</TableCell>
                          <TableCell><Chip label={c.rank || '-'} size="small" /></TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {c.base || '-'}
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {c.expiryDate ? format(new Date(c.expiryDate), 'dd/MM/yyyy') : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={c.urgency === 0 ? 'Missing' : c.urgency === 1 ? 'Overdue' : 'Expiring'}
                              size="small"
                              color={c.urgency <= 1 ? 'error' : 'warning'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {!suggestedGroups && !loading && (
        <Typography color="text.secondary" variant="body2">
          Select a subject and target date above, then click "Suggest Groups" to auto-group crew.
        </Typography>
      )}
    </Box>
  );
};

export default ClassBuilder;
